/**
 * CloudItemCard - 云端收藏夹/标签推荐卡片
 * 用于 Square 初始态展示推荐内容
 */

import React from 'react';
import { enhanceSubscriberCount } from '../utils/postList';

export interface TopBlogger {
  avatar: string;
  followersCount?: number;
}

export interface CloudItemCardData {
  id: string;
  name: string;
  description?: string | null;
  postCount?: number;
  subscriberCount?: number;
  topImages?: string[];
  topBloggers?: TopBlogger[];
  uploader?: {
    username?: string;
    avatarUrl?: string;
  };
}

export interface CloudItemCardProps {
  item: CloudItemCardData;
  onClick?: () => void;
}

/**
 * 重叠排列的博主头像组件
 * 三个头像：第一个靠下，第二个靠上（中间最大），第三个又靠下
 */
const OverlappingAvatars: React.FC<{ bloggers: TopBlogger[] }> = ({ bloggers }) => {
  if (bloggers.length === 0) return null;

  // 头像大小配置：中间最大，两边较小
  const sizes = [30, 42, 30];
  // 垂直位置：使用 bottom 对齐，中间的更高
  const bottoms = [0, 5, 1];
  // 水平位置：从右到左排列
  const rights = [0, 15, 42];
  // z-index：第一个在最前面
  const zIndexes = [3, 2, 1];
  
  // 显示的头像（最多3个）
  const displayBloggers = bloggers.slice(0, 3);

  return (
    <div style={{
      position: 'relative',
      bottom: 20,
      width: 80,
      height: 66,
      flexShrink: 0,
    }}>
      {displayBloggers.map((blogger, index) => {
        const size = sizes[index] || 30;
        const bottom = bottoms[index] || 0;
        const right = rights[index] || 0;
        const zIndex = zIndexes[index] || 1;
        
        return (
          <img
            key={index}
            src={blogger.avatar}
            alt={`blogger-${index}`}
            style={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid var(--ant-color-bg-container)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              right,
              bottom,
              zIndex,
            }}
          />
        );
      })}
    </div>
  );
};

export const CloudItemCard: React.FC<CloudItemCardProps> = ({
  item,
  onClick,
}) => {
  if (!item || !item.id) return null;

  const images = item.topImages?.slice(0, 3) || [];
  const bloggers = item.topBloggers || [];

  // 生成随机旋转角度
  const getRandomRotation = () => {
    const angle = 0.8 + Math.random() * 2.2; 
    const direction = Math.random() > 0.5 ? 1 : -1; // 正负方向
    return angle * direction;
  };

  // 边框层样式 - 使用 mask 镂空技术实现渐变边框
  const borderLayerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0, // 撑满父容器
    borderRadius: 22,
    padding: '1.2px',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.06) 100%)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor', // 兼容 Chrome/Safari/Edge
    maskComposite: 'exclude', // 标准属性 (Firefox 等)
    pointerEvents: 'none', // 关键！让鼠标点击能穿透边框层
    zIndex: 1,
  };

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        backgroundColor: 'var(--ant-color-bg-container)',
        borderRadius: 22,
        padding: 20,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 0px rgba(0,0,0,0.06)',
      }}
      onMouseEnter={(e) => {
        const rotation = getRandomRotation();
        e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = `translateY(-2px) rotate(${rotation}deg)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 0px rgba(0,0,0,0.06)';
        e.currentTarget.style.transform = 'translateY(0) rotate(0deg)';
      }}
    >
      {/* 渐变边框层 */}
      <div style={borderLayerStyle} />

      {/* 头部：标题 + 头像 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          {/* 标题 */}
          <h3 style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--ant-color-text)',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.name}
          </h3>
          
          {/* 描述 */}
          {item.description && (
            <p style={{
              margin: '4px 0 0 0',
              fontSize: 13,
              color: 'var(--ant-color-text-secondary)',
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {item.description}
            </p>
          )}
        </div>

        {/* Top 博主头像（重叠排列） */}
        <OverlappingAvatars bloggers={bloggers} />
      </div>

      {/* 统计信息 */}
      <div style={{
        fontSize: 13,
        color: 'var(--ant-color-text-tertiary)',
        marginBottom: 12,
      }}>
        {item.postCount || 0} 帖 · {enhanceSubscriberCount(item.subscriberCount || 0, item.id)} 订阅
      </div>

      {/* 底部：3张预览图 */}
      {images.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6,
        }}>
          {images.map((imageUrl, index) => (
            <div
              key={index}
              style={{
                aspectRatio: '1',
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: 'var(--ant-color-fill-tertiary)',
              }}
            >
              <img
                src={imageUrl}
                alt={`preview-${index}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
          ))}
          {/* 补齐空位 */}
          {images.length < 3 && Array.from({ length: 3 - images.length }).map((_, index) => (
            <div
              key={`empty-${index}`}
              style={{
                aspectRatio: '1',
                borderRadius: 8,
                backgroundColor: 'var(--ant-color-fill-quaternary)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CloudItemCard;
